import Vue from 'vue';
import get from "lodash/get";
import { isNullOrEmpty, nowInStoreTz, buildScheduledTargetDate } from "@/services/util.service"
import { scanSchedule, isInSchedule, compareSchedules } from "@/services/schedule.service"
import { fetchUpsellRecommendations, fetchPopularItems } from "@/services/ai-upselling.api"

// DEV-11345: the landing route (router.js getVisibleMenus) downloads each full menu only to check
// it has a visible category; the store page's fetchAllMenus then downloaded the same menus again.
// The landing check hands its responses over once instead. Keyed by store + menu + language (the
// response depends on Accept-Language); one-shot and 30s-bounded so every later refresh of the
// menu still goes to the network, exactly as before.
const handedOverMenus = new Map()
const menuKey = (storeId, menuCode) => `${storeId}|${menuCode}|${get(Vue.prototype, '$vuetify.lang.current', '')}`
export const handOverMenu = (storeId, menuCode, data) => {
  handedOverMenus.set(menuKey(storeId, menuCode), { data, at: Date.now() })
}
const takeHandedOverMenu = (storeId, menuCode) => {
  const key = menuKey(storeId, menuCode)
  const entry = handedOverMenus.get(key)
  handedOverMenus.delete(key)
  return entry && Date.now() - entry.at < 30000 ? entry.data : null
}

const parseMenuItem = (state, code) => {
  const menuGroups = get(state, 'menu.menuGroups', []) || []
  let menuItems = menuGroups.map(menuGroup => menuGroup.menuItems) || []
  const allItems = menuItems.reduce((prev, curr) => [ ...prev, ...curr], [])
  return allItems.find(menuItem => menuItem.code === code)
}

// getAiRecommendationsCategory's own thresholds -- shared with fetchAiRecommendations'
// upselling->popular-items fallback below so both agree on what "not enough to show"
// means, instead of drifting if only one side were ever hand-tuned later.
const AI_RECOMMENDATIONS_MIN_TO_SHOW = 3

// A recommendation's price comes from the menu item its code points at; the AI response's
// own price is only a fallback when that menu item carries no charges.
const recommendationCharges = (realItem, rec) => {
  const realCharges = get(realItem, 'charges', []) || []
  return realCharges.length
    ? realCharges
    : [{ code: 'defCharge', type: 'OT', forMembership: false, promotional: false, price: rec.price || 0 }]
}
const AI_RECOMMENDATIONS_MAX_TO_SHOW = 6

// DEV-11115: the cart's "You may also like" row shows up to 3 and -- unlike the menu
// category above -- has NO floor: 1 or 2 available picks is still a useful row there,
// so min_items is sent as 1. (The category sends 3 because it hides below 3; reusing
// AI_RECOMMENDATIONS_MIN_TO_SHOW here would blank the cart row whenever only 2 items
// qualify.) Over-fetches 5 so the sold-out/not-on-the-live-menu filter below can drop
// a couple and still likely leave 3.
export const AI_CART_RECOMMENDATIONS_TO_SHOW = 3
const AI_CART_RECOMMENDATIONS_TO_FETCH = 5
const AI_CART_RECOMMENDATIONS_MIN_ITEMS = 1
// Bumped per fetch so a slower, older response (e.g. the guest call still in flight
// when the guest logs in) can't overwrite a newer one.
let _aiCartRequestToken = 0
// Same guard for the menu category: a quick Delivery -> Pickup switch must not let the
// slower Delivery response land last and show Delivery picks while on Pickup.
let _aiMenuRequestToken = 0
// DEV-11453: same guard for the menu being opened. fetchAllMenus loads every menu one after
// another before it sets the target, so opening Dessert, going back and opening Dinner left
// two calls running -- and Dessert's SET_MENU could land last, showing Dessert under Dinner.
let _fetchAllMenusToken = 0

// Same availability rule the category getter applies (real, in-stock item on the live
// menu) -- pulled out so fetchAiRecommendations can ask "would this response actually
// produce a visible category" without re-fetching/duplicating the getter's own filter.
const countAvailableRecommendations = (recommendations, findRealMenuItemByCode) => {
  return (recommendations || []).filter(rec => {
    const realItem = findRealMenuItemByCode(rec.code)
    return !!realItem && !realItem.outOfStock
  }).length
}

export default {
  state: {
    menu: {},
    allMenus: null,
    liteMenus: [],
    assignedMenus: [],
    thumbsUpData: [],
    aiRecommendations: [],
    aiCartRecommendations: [],
    aiCartRecommendationsLoading: false
  },
  getters: {
    isCategoryAvailable: (state, getters) => (category) => {
      if (!category.schedule || !category.schedule.items || category.schedule.items.length === 0) {
        return true;
      }

      const store = getters.getStore;
      const storeTz = (store && (store.timeZone || store.timezone)) || null;
      const scheduledDay = get(getters.getCheckoutInfo, "scheduledDay", null);
      const scheduledTime = get(getters.getCheckoutInfo, "scheduledTime", null);

      let targetDate;

      if (scheduledDay) {
        targetDate = new Date(scheduledDay);

        if (scheduledTime) {
          if (scheduledTime === "now") {
            const now = nowInStoreTz(storeTz);
            targetDate.setHours(now.getHours());
            targetDate.setMinutes(now.getMinutes());
            targetDate.setSeconds(now.getSeconds());
          } else if (!isNaN(Date.parse(scheduledTime))) {
            const parsedTime = new Date(scheduledTime);
            targetDate.setHours(parsedTime.getHours());
            targetDate.setMinutes(parsedTime.getMinutes());
            targetDate.setSeconds(parsedTime.getSeconds());
          } else {
            console.error(`Invalid scheduledTime format: ${scheduledTime}`);
            targetDate.setHours(0);
            targetDate.setMinutes(0);
            targetDate.setSeconds(0);
          }
        }
      } else {
        targetDate = nowInStoreTz(storeTz);
      }
      return isInSchedule(
        category.schedule.items,
        targetDate,
        0
      );
    },
    getMenu(state) {
      return state.menu
    },
    getLiteMenus(state) {
      return state.liteMenus
    },
    getAvailableMenus(state, getters) {
      return (getters.getMenus || [])
        .filter(_menu => getters.isMenuChannelAllowed(_menu))
        .sort((a,b) => {
          const aNum = getters.isMenuAvailable(a) ? 1 : -1
          const bNum = getters.isMenuAvailable(b) ? 1 : -1

          // Show available menus before unavailable
          return aNum > bNum ? -1 : 1
        })
    },
    getAssignedMenus(state) {
      return state.assignedMenus;
    },
    getAllMenus(state) {
      return state.allMenus
    },
    getMenus(state, getters) {
      const store = getters.getStore;
      if (!store) {
        console.error('Error: Store is undefined');
        this.$api.post("/order/error", {
          error: "store is undefined"
        });
        return []; 
      } 
      const storeId = store.storeId
      if (!store.menus) {
        return [];
      }
      const menus = Object.keys(store.menus).map(code => (store.menus[code]));

      return menus.sort((a, b) => {
        return a.sequence - b.sequence;
      });
    },
    getMenuScheduleLimit: (state, getters) => {
      const pickupHoursLimit = get(getters.getMenu, 'scheduledPickupTimeLimitInHours', 0)
      const deliveryHoursLimit = get(getters.getMenu, 'scheduledDeliveryTimeLimitInHours', 0)

      if (getters.isPickUp && pickupHoursLimit > 0)
        return pickupHoursLimit
      if (getters.isDelivery && deliveryHoursLimit > 0)
        return deliveryHoursLimit
      return 0
    },
    getMenuSchedule: (state, getters) => (menu=getters.getMenu) => {
      return get(menu, 'schedule.items', [])
    },
    menuAvailableTime: (state, getters) => (menu=getters.getMenu) => {
      return scanSchedule(getters.getMenuSchedule(menu))
    },
    isMenuChannelAllowed: (state, getters) => (menu=getters.getMenu) => {
      const channels = get(getters.getStore, `menus[${menu.code}].channels`, []) || []

      // Old stores will not includes channels until used
      // So undefined or empty means allow all
      return isNullOrEmpty(channels) || channels.includes('ONLINE')
    },
    requiresMenu(state, getters) {
      return getters.isDineIn && get(getters.getStore, 'settings.orderSettings.restrictMenuOnDiningTable')
    },
    requiresTable(state, getters) {
      // When in dine in with this flag, they must set the table num before adding to cart
      // When true, add to cart should be disabled
      return getters.requiresMenu && !getters.isDineInTableSet
    },
    assignedMenuIsAllowed: (state, getters) => (menu=getters.getMenu) =>  {
      if (getters.requiresTable)
        return false

      if (getters.requiresMenu) {
        const _assignedMenus = get(getters.getServerCart, 'assignedMenus', []) || []
        return !isNullOrEmpty(_assignedMenus) && _assignedMenus.includes(menu.code)
      }
      return true
    },

    isMenuAvailable: (state, getters) => (menu=getters.getMenu) => {
      const store = getters.getStore
      const storeTz = (store && (store.timeZone || store.timezone)) || null
      return getters.isMenuChannelAllowed(menu) && getters.assignedMenuIsAllowed(menu) &&
        isInSchedule(
          getters.getMenuSchedule(menu),
          nowInStoreTz(storeTz),
          getters.getMenuScheduleLimit
        )
    },
    getMenuItemsAndGroups(state, getters) {
      if(state.menu.menuGroups) {
        return state.menu.menuGroups
          .map(cat => {
            const isAvailable = getters.isCategoryAvailable(cat);
            if (!isAvailable) {
              return null; 
            }
            return { 
              name: cat.name, 
              description: cat.description, 
              items: cat.menuItems, 
              code: cat.code, 
              hidden: cat.hidden, 
              schedule: cat.schedule 
            };
          })
          .filter(Boolean); // Remove null entries
      }
    },
    // ,
    // getMenuItem: (state) => (code) => {
    //   return parseMenuItem(state, code)
    // }
    getItemThumbsUp: (state) => (itemCode) => {
      const item = state.thumbsUpData.find(t => t.itemCode === itemCode);
      return item ? item.thumbup : 0;
    },
    // Plain lookup, no merging (unlike cart.js's findFullItem, which merges a passed-in
    // item's own fields back on top -- correct for a real cart item carrying user config,
    // wrong here since an AI-suggestion's thin synthetic fields (attributes, relations,
    // charges) would then win over the real item's actual ones). Searches every loaded
    // menu, not just the current one, since a recommendation can name an item from a
    // different menu (e.g. menu_874_COPY_menu_935_categories_10_items_498).
    findRealMenuItemByCode: (state, getters) => (code) => {
      for (const menu of (getters.getAllMenus || [])) {
        for (const group of (menu.menuGroups || [])) {
          const found = (group.menuItems || []).find(item => item.code === code)
          if (found) return found
        }
      }
      return null
    },
    // Synthetic category, not part of getMenuGroups/menuGroups — shaped to satisfy
    // the same fields StorePage.vue's filteredCategories/menu-item-card read
    // (code, name, anchor, items[].charges/tags), so it can be prepended to the
    // rendered category list without either of those needing to know it's not real.
    // name/tag/reason arrive from the backend as {translations: [{lang, value}]}, same
    // shape as menu item names elsewhere -- kept as *Translations here rather than
    // resolved to a display string, since getters aren't reactive to $vuetify.lang.current
    // (a Vue instance property, not Vuex state) and would go stale on a language switch.
    // Components resolve these via matchTranslation in a computed, same as everywhere
    // else in the app that displays a translations array.
    getAiRecommendationsCategory(state, getters) {
      if (!state.aiRecommendations.length) return null

      // The AI service only returns shallow fields (code, name, price, tag) -- no
      // modifiers/attributes/relations. Real config data comes from findRealMenuItemByCode
      // (cross-menu-by-code, since a recommendation can name an item from a menu other
      // than the one currently being viewed, e.g. menu_874_COPY_menu_935_categories_10_items_498).
      // fetchAiRecommendations over-fetches (count: 10) specifically so this can drop
      // sold-out items (per DEV-10387's FE responsibilities) and still likely have enough
      // left. An item missing from the live menu entirely (findRealMenuItemByCode returns
      // null -- e.g. removed since the AI service's last cache refresh) is treated the
      // same as sold out: nothing to show/add-to-cart for it.
      const availableRecs = state.aiRecommendations.filter(rec => {
        const realItem = getters.findRealMenuItemByCode(rec.code)
        return !!realItem && !realItem.outOfStock
      })
      const shownRecs = availableRecs.slice(0, AI_RECOMMENDATIONS_MAX_TO_SHOW)
      // Hide the whole category rather than show a sparse, half-empty row.
      if (shownRecs.length < AI_RECOMMENDATIONS_MIN_TO_SHOW) return null

      return {
        code: 'ai-recommendations',
        // "For You" implies personalization, which is only true for the logged-in
        // /ai/upselling path -- a logged-out guest gets /ai/popular-items (anonymous
        // best-sellers, not personalized to them at all), so the title must not claim
        // otherwise.
        name: getters.isLoggedIn
          ? Vue.prototype.$vuetify.t('$vuetify.storePage.aiRecommendationsCategory')
          : Vue.prototype.$vuetify.t('$vuetify.storePage.aiRecommendationsCategoryGuest'),
        anchor: 'ai-recommendations',
        // Start from the REAL item, not a thin object built from just the AI response --
        // otherwise the card is missing everything menuItemCard(Custom).vue reads off a
        // normal item: description, promo tag, calories label, thumbs-up count, order
        // limit, etc. (all real fields the old {code, name, price, charges}-only object
        // never carried). Name is ALSO taken from the real item (realItem.name, via the
        // spread below), not the AI response's own name field -- the AI service's name
        // can go stale relative to a rename in BC2 (code is a stable identifier,
        // independent of the item's display name, so a rename never changes which real
        // item this card points at, but it can leave the AI service's cached copy of the
        // name outdated until its own cache refreshes). Only tag/reason -- fields with no
        // real-item equivalent -- are taken from the AI response.
        items: shownRecs.map(rec => {
          const realItem = getters.findRealMenuItemByCode(rec.code)
          return {
            ...realItem,
            // Real menu items don't carry a self-referential pointer to their own menu --
            // StorePage.vue's categoriesWithAiRecommendations filters on this (defense in
            // depth against a recommendation naming an item from a menu other than the
            // one on screen), so it's still derived from the AI response's code here.
            menuCode: (rec.code || '').split('_categories_')[0],
            aiTagTranslations: get(rec.tag, 'translations', []),
            aiTagCode: rec.tag_code,
            aiReasonTranslations: get(rec.reason, 'translations', []),
            // menuItemCard.vue/menuItemCardCustom.vue's handleOrderItem checks this flag and
            // opens the real item (by code) instead of this display copy, so the config
            // dialog shows the item's actual, live options.
            isAiSuggestion: true,
            charges: recommendationCharges(realItem, rec),
          }
        })
      }
    },
    getAiCartRecommendationsLoading(state) {
      return state.aiCartRecommendationsLoading
    },
    // DEV-11115: the cart row's items. Same shape/derivation as
    // getAiRecommendationsCategory's items above (real item first, tag/reason from the
    // AI response, isAiSuggestion so the add button opens the real item when it needs
    // configuring) -- just no synthetic category wrapper, no minimum, capped at 3.
    getAiCartRecommendations(state, getters) {
      if (!state.aiCartRecommendations.length) return []

      // Picks already in the cart are hidden, and come back as soon as they leave it: the
      // fetched list isn't refreshed on add/remove, so this filter alone makes a card vanish
      // when added and reappear when removed. Fetching more than are shown lets the next
      // pick fill the gap.
      const inCartCodes = new Set((getters.getServerCartItems || []).map(item => item.code))

      // "Available right now" is more than just in-stock: an item can be disabled, or
      // outside its own schedule / the store's for the time being ordered for (a
      // breakfast-only item at 8pm). The candidate pool comes from sales history, which
      // knows nothing about either -- so apply the same rule the menu card applies
      // (menuItemCardCustom's isUnavailable: disabled || outOfStock || isOutOfSchedule),
      // reusing the same helpers rather than a second interpretation of availability.
      const targetDate = buildScheduledTargetDate(getters.getCheckoutInfo)
      const storeTz = get(getters.getStore, 'timezone', null) || null
      const isAvailableNow = (realItem) => {
        if (realItem.disabled || realItem.outOfStock) return false
        return compareSchedules([
          { schedule: getters.getStoreSchedule, advance: getters.getHoursLimit },
          { schedule: get(realItem, 'schedule.items', []), advance: get(realItem, 'advance', 0) }
        ], targetDate, storeTz)
      }

      return state.aiCartRecommendations
        .filter(rec => {
          if (inCartCodes.has(rec.code)) return false
          const realItem = getters.findRealMenuItemByCode(rec.code)
          return !!realItem && isAvailableNow(realItem)
        })
        .slice(0, AI_CART_RECOMMENDATIONS_TO_SHOW)
        .map(rec => {
          const realItem = getters.findRealMenuItemByCode(rec.code)
          // Everything shown -- name, image, price -- comes from the menu item the code points
          // at; the code, tag/reason text, and (only if the menu item has none) price come
          // from the AI response.
          return {
            ...realItem,
            menuCode: (rec.code || '').split('_categories_')[0],
            aiTagTranslations: get(rec.tag, 'translations', []),
            aiTagCode: rec.tag_code,
            aiReasonTranslations: get(rec.reason, 'translations', []),
            isAiSuggestion: true,
            charges: recommendationCharges(realItem, rec),
          }
        })
    }
  },
  actions: {
    async fetchAiRecommendations({ commit, getters }) {
      if (!get(getters.getStore, 'settings.owaSettings.aiRecommendationCategoryEnabled', false)) {
        commit('SET_AI_RECOMMENDATIONS', [])
        return
      }
      const { storeId } = getters.getStore || {}
      // getMenu/state.menu is NOT reliably synced to the menu currently being viewed --
      // it's only populated by fetchAllMenus(mid), which router.js's global guard skips
      // entirely on a same-store menu switch (only fires on a store change), and
      // StorePage.vue's mounted() (where this action is dispatched) doesn't even re-run
      // on that same navigation since Vue Router reuses the component instance. Read mid
      // straight from the current route instead -- same $router.history.current access
      // pattern used elsewhere in cart.js -- so this can't send a stale/wrong menu code.
      const mid = get(this, '$router.history.current.params.mid', null)
      if (!storeId) {
        commit('SET_AI_RECOMMENDATIONS', [])
        return
      }
      // DEV-10999: menu_code went from "strongly recommended" to load-bearing -- the AI
      // service reads the merchant's per-item aiRecommended flags off the named menu, so
      // without it the service falls back to UNFILTERED results rather than erroring.
      // Route params legitimately omit mid on single-menu stores, so this can't be a hard
      // failure; log it so a store showing non-recommended items is diagnosable.
      if (!mid) {
        console.warn('[AI recommendations] no menu code on route -- item-level AI Recommended flags will not be applied')
      }

      // DEV-10387: logged-in gets the personalized /ai/upselling call (needs phone/auth
      // to build a profile); a logged-out guest gets /ai/popular-items instead -- an
      // anonymous best-sellers fallback, no phone/auth required, no LLM call. count: 10
      // over-fetches so getAiRecommendationsCategory can drop sold-out items and still
      // have enough left to show up to 6 (see that getter's own filtering).
      //
      // Follow-up: a logged-in user's upselling call can error, or come back with too few
      // items that survive the getter's own availability filter (sold-out/removed) to
      // ever clear AI_RECOMMENDATIONS_MIN_TO_SHOW -- in either case the category would
      // just never show, even though the anonymous popular-items fallback might well have
      // enough. Falls back to it rather than leaving a personalized user with nothing.
      const requestToken = ++_aiMenuRequestToken
      try {
        const orderType = getters.getOrderType
        let recommendations = []
        if (getters.isLoggedIn) {
          try {
            recommendations = await fetchUpsellRecommendations({
              phone: getters.getPhone || get(getters.getCheckoutInfo, 'phoneNumber', null),
              storeId, mid, authToken: getters.getAuthToken, count: 10, orderType,
              minItems: AI_RECOMMENDATIONS_MIN_TO_SHOW,
            })
          } catch (upsellError) {
            // Not found (no profile for this phone) is expected for new customers —
            // fall through to popular-items below same as an empty/insufficient response.
          }
          if (countAvailableRecommendations(recommendations, getters.findRealMenuItemByCode) < AI_RECOMMENDATIONS_MIN_TO_SHOW) {
            recommendations = await fetchPopularItems({
              storeId, mid, count: 10, orderType, minItems: AI_RECOMMENDATIONS_MIN_TO_SHOW,
            })
          }
        } else {
          recommendations = await fetchPopularItems({
            storeId, mid, count: 10, orderType, minItems: AI_RECOMMENDATIONS_MIN_TO_SHOW,
          })
        }
        if (requestToken === _aiMenuRequestToken) commit('SET_AI_RECOMMENDATIONS', recommendations)
      } catch (error) {
        // Both calls failed (or the guest-only popular-items call itself failed) --
        // no AI row shows; never blocks the menu.
        if (requestToken === _aiMenuRequestToken) commit('SET_AI_RECOMMENDATIONS', [])
      }
    },
    // DEV-11115: the cart's "You may also like" row. Deliberately separate from
    // fetchAiRecommendations above rather than parameterized onto it: different store
    // flag, different count/min_items, sends cart_items, and is re-dispatched after
    // every add -- so sharing one action/state would mean the two surfaces fighting
    // over the same array with the wrong tuning for whichever wrote last.
    async fetchAiCartRecommendations({ commit, getters }) {
      // Own store flags (DEV-11147 cart, DEV-11342 checkout), independent of
      // aiRecommendationCategoryEnabled. Both surfaces show this same list (same request,
      // same cart), so it is fetched when either is on; each surface gates its own
      // display on its own flag. Both off means no call at all.
      const owaSettings = get(getters.getStore, 'settings.owaSettings', {}) || {}
      if (!owaSettings.aiRecommendationCartEnabled && !owaSettings.aiRecommendationCheckoutEnabled) {
        commit('SET_AI_CART_RECOMMENDATIONS', [])
        return
      }
      const { storeId } = getters.getStore || {}
      // Same reason as fetchAiRecommendations: state.menu can be stale on a same-store
      // menu switch, so mid comes from the live route. The checkout route has no mid,
      // though, and without one the service ignores the item-level AI Recommended flags --
      // fall back to the current menu there, same order cart.js uses for /shoppingcart.
      const mid = get(this, '$router.history.current.params.mid', null) || get(getters.getMenu, 'code', null)
      if (!storeId) {
        commit('SET_AI_CART_RECOMMENDATIONS', [])
        return
      }

      // Codes currently in the cart. Sent so the model complements them (and so it
      // won't return them); re-sending after each add is what refreshes the row.
      const cartItems = (getters.getServerCartItems || []).map(item => item.code).filter(Boolean)
      const orderType = getters.getOrderType || null

      const requestToken = ++_aiCartRequestToken
      commit('SET_AI_CART_RECOMMENDATIONS_LOADING', true)
      try {
        let recommendations = []
        if (getters.isLoggedIn) {
          try {
            recommendations = await fetchUpsellRecommendations({
              phone: getters.getPhone || get(getters.getCheckoutInfo, 'phoneNumber', null),
              storeId, mid, authToken: getters.getAuthToken,
              count: AI_CART_RECOMMENDATIONS_TO_FETCH,
              minItems: AI_CART_RECOMMENDATIONS_MIN_ITEMS,
              orderType, cartItems,
            })
          } catch (upsellError) {
            // No profile for this phone yet is expected for a new customer -- fall
            // through to popular-items rather than showing nothing.
          }
          if (!countAvailableRecommendations(recommendations, getters.findRealMenuItemByCode)) {
            recommendations = await fetchPopularItems({
              storeId, mid, count: AI_CART_RECOMMENDATIONS_TO_FETCH,
              minItems: AI_CART_RECOMMENDATIONS_MIN_ITEMS, orderType, cartItems,
            })
          }
        } else {
          recommendations = await fetchPopularItems({
            storeId, mid, count: AI_CART_RECOMMENDATIONS_TO_FETCH,
            minItems: AI_CART_RECOMMENDATIONS_MIN_ITEMS, orderType, cartItems,
          })
        }
        if (requestToken === _aiCartRequestToken) commit('SET_AI_CART_RECOMMENDATIONS', recommendations)
      } catch (error) {
        // Empty or failed -- the row hides itself. Never an error state in the cart.
        if (requestToken === _aiCartRequestToken) commit('SET_AI_CART_RECOMMENDATIONS', [])
      } finally {
        if (requestToken === _aiCartRequestToken) commit('SET_AI_CART_RECOMMENDATIONS_LOADING', false)
      }
    },
    // Cart.vue is mounted at page load (non-lazy v-dialog), so an empty cart can't fetch
    // from there without firing the AI call for every visitor. The headers call this when
    // the cart dialog opens instead. A non-empty cart is already kept fresh by Cart.vue's
    // add watcher, and picks already loaded for the empty cart are reused across opens.
    fetchAiCartRecommendationsOnOpen({ dispatch, getters, state }) {
      // The fetch also runs for the checkout flag alone -- don't spend an AI call on a cart
      // row that won't show.
      if (!get(getters.getStore, 'settings.owaSettings.aiRecommendationCartEnabled', false)) return
      if ((getters.getServerCartItems || []).length) return
      if (state.aiCartRecommendations.length || state.aiCartRecommendationsLoading) return
      return dispatch('fetchAiCartRecommendations')
    },
    async fetchAssignedMenus({ commit, getters }, tid) {
      const { storeId } = getters.getStore;
      try {
        const response = await this.$api.get(`/assigned_menu?tableId=${tid}`, {
          headers: { storeId }
        });
        commit("SET_ASSIGNED_MENUS", response.data['menuIds']);
        return response.data['menuIds'];
      } catch (error) {
        console.error('Failed to fetch assigned menu', error);
        throw error;
      }
    },
    async fetchLiteMenus({ commit, getters, dispatch }, setMenu=null) {
      const { storeId } = getters.getStore;
      const orderType = getters.getOrderType || 'DINE_IN';

      try {
        const response = await this.$api.get(`/lite_menus?orderType=${orderType}`, { headers: { storeId } });
        commit('SET_LITE_MENUS', response.data);
        return response.data; // Return the data to the caller

      } catch (error) {
        this.$api.post("/order/error", {
          error: "fetchLiteMenus function ERROR" + error,
          storeId
        });
        console.error("Failed to fetch menu", error);
        dispatch("clearMenu", error);
        throw error; // Re-throw the error to propagate it to the caller
      }
    },
    setMenu({ commit, dispatch, getters }, {store, mid, gid, sid}) {
      var menu = store.menus[mid]

      if (menu && 'code' in menu) {
        return this.$api.get(`/menu/${menu.code}`, { headers: { storeId: store.storeId }}).then(data => {
          commit('SET_MENU', data.data)
        }).catch(e => {
          console.error('failed to fetch menu', e)

          // If the current store has a menu, but the api request failed and doesnt match the cached menu, clear the state
          // If the request failed but we have menu information cached for this menu, it's better to show that than nothing
          if (getters.getMenu.code != menu.code) {
            dispatch('clearMenu', e)
          }
        })
      } else {
        if (gid && sid) {
          // If the requested menu doesnt exist, redirect to the menu selection page to try to show another
          this.$router.push({
            name: 'menu-selection',
            params: {gid, sid, restaurantSlug:  get(getters, 'getStore.slug', '')}
          }).catch(() => {})
        }
      }
    },

    setMenuItem({ commit }, newItem) {
      commit('SET_MENU_ITEM', newItem)
    },

    clearMenu({ commit, dispatch }, message) {
      dispatch('showSnack', { text: message, color: 'error'})

      commit('SET_MENU', {
        menuGroups: [{
          name: Vue.prototype.$vuetify.t('$vuetify.menu.noMenu'),
          menuItems: [{}],
          code: 404
        }]
      })
    },

    async fetchAllMenus({ commit, getters, dispatch }, setMenu=null) {
      let menus = []
      let found = false
      const { storeId, groupId, slug } = getters.getStore
      // Only calls that pick the menu to show take part; the newest one wins (DEV-11453).
      const requestToken = setMenu ? ++_fetchAllMenusToken : null
      const isStale = () => requestToken !== null && requestToken !== _fetchAllMenusToken

      // DEV-11345: thumbs-up and every menu used to be requested one after another (each awaited
      // before the next even started), so the menu on screen waited for all of them in sequence.
      // They now start together; responses are still processed below in the same order, with the
      // same commits and the same per-menu error handling as before.
      const storeMenus = getters.getMenus
      const thumbsUpRequest = this.$api.get(
        `item/get_thumbup/${groupId}/${storeId}`,
        { headers: { storeId } }
      ).catch(e => {
        console.error("Failed to fetch thumbup data", e);
        return null
      })
      // Settled into { res } / { error } right away so a failing menu is never an unhandled
      // rejection while earlier menus are still being processed.
      const menuRequests = storeMenus.map(menu => {
        const handedOver = takeHandedOverMenu(storeId, menu.code)
        const request = handedOver
          ? Promise.resolve({ data: handedOver })
          : this.$api.get(`/menu/${menu.code}`, { headers: { storeId }})
        return request.then(res => ({ res }), error => ({ error }))
      })

      const thumbsUpResponse = await thumbsUpRequest
      // Store the thumbsUp data in our state
      if (thumbsUpResponse && thumbsUpResponse.data) {
        commit("SET_THUMBS_UP_DATA", thumbsUpResponse.data);
      }

      for (let menuIndex = 0; menuIndex < storeMenus.length; menuIndex++) {
        const menu = storeMenus[menuIndex]
        await menuRequests[menuIndex].then(({ res, error }) => {
          if (error) throw error
          const menuData = res.data;
          if (menuData && menuData.menuGroups) {
            menuData.menuGroups.forEach(group => {
              if (group.menuItems) {
                group.menuItems.forEach(item => {
                  const thumbsUp = getters.getItemThumbsUp(item.code);
                  item.thumbup = thumbsUp;
                });
              }
            });
          }

          if (setMenu && menu.code == setMenu) {
            found = true;
            if (!isStale()) commit('SET_MENU', menuData);
          }

          menus.push(menuData);
        }).catch(e => {
          console.error('failed to fetch menu', e)

          // If the current store has a menu, but the api request failed and doesnt match the cached menu, clear the state
          // If the request failed but we have menu information cached for this menu, it's better to show that than nothing
          // When a specific target menu is requested, skip clearMenu for non-target menu failures
          if (setMenu && menu.code != setMenu) return
          if (isStale()) return
          if (getters.getMenu.code != menu.code) {
            dispatch('clearMenu', e)
          }
        })
      }

      // If the user provided a menu code via the url that does not exist in the stores list, redirect them to a valid one
      // (not from a superseded call -- the guest has already moved on to another menu)
      if (!!setMenu && !found && !isStale()) {
        const tid = this.$route.query["tid"];
        // Generally we allow menu selection to handle this for consistency but this avoids a bouncing around routes affect
        if (getters.getMenus.length == 1) {
          commit('SET_MENU', menus[0])
          this.$router.replace({
            name: 'store',
            params: {
              gid: groupId,
              sid: storeId,
              mid: menus[0].code,
              restaurantSlug: slug
            },
            query: { tid }
          }).catch(() => {})
        } else {
          this.$router.replace({
            name: "menu-selection",
            params: { storeId, groupId, restaurantSlug: slug },
            query: { tid }
          }).catch(() => {});
        }
      }

      commit('SET_ALL_MENUS', menus)
    },

    setCouponItemDialog({ commit }, payload) {
      commit('SET_COUPON_DIALOG', payload)
    },

    removeCoupon({ commit, dispatch, getters }) {
      // New UI Method
      commit('SET_COUPON_DIALOG', null)
      commit('SET_ACTIVE_COUPON', null)
      commit('SET_COMPOSITE_QUEUE', [])

      // Old manual input method
      dispatch('setCouponCode', {
        storeId: get(getters, 'getStore.storeId', 0),
        couponCode: null
      })
    },

    removePunchCards({ getters }) {
      const punchCards = get(getters, 'getPunchCards', [])
      if (punchCards && Array.isArray(punchCards)) {
        for (let punchCard of punchCards) {
          punchCard.using = null
          delete punchCard.using
        }
      }
    },

    fetchOptions( { commit, getters }) {
      const store = getters.getStore;
      if (!store || !store.storeId) return Promise.resolve();
      const { storeId } = store;
      const orderType = getters.getOrderType;
      return this.$api.get(`/rewards?orderType=${orderType}`, { headers: { storeId } }).then(res => {
        commit('SET_REWARDS', res.data)
      })
    }
  },
  mutations: {
    SET_MENU(state, menu) {
      state.menu = menu
    },
    SET_LITE_MENUS(state, liteMenus) {
      state.liteMenus = liteMenus;
    },
    SET_ALL_MENUS(state, menus) {
      state.allMenus = menus
    },
    SET_MENU_ITEM(state, newItem) {
      let currentItem = parseMenuItem(state, newItem.code)

      currentItem = { currentItem, ...newItem}
    },
    GET_MENU_ITEM(state, code) {
      return state.menu.menuGroups.find(menuItem => menuItem.code === code)
    },
    SET_ASSIGNED_MENUS(state, assignedMenus) {
      state.assignedMenus = assignedMenus;
    },
    SET_THUMBS_UP_DATA(state, data) {
      state.thumbsUpData = data;
    },
    SET_AI_RECOMMENDATIONS(state, recommendations) {
      state.aiRecommendations = recommendations || [];
    },
    SET_AI_CART_RECOMMENDATIONS(state, recommendations) {
      state.aiCartRecommendations = recommendations || [];
    },
    SET_AI_CART_RECOMMENDATIONS_LOADING(state, loading) {
      state.aiCartRecommendationsLoading = !!loading;
    }
  }
}
